import React, { useState, useEffect } from 'react';
import { Save, Key, Bell, DollarSign, Globe, Smartphone, Mail } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        company: {},
        mpesa: {},
        africastalking: {},
        notifications: {}
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
    try {
        const tenantResponse = await api.get('/tenants/current');
        setSettings(prev => ({ ...prev, company: tenantResponse.data.data }));
        
        try {
            const mpesaResponse = await api.get('/tenants/credentials/mpesa');
            // Handle both formats: direct object or nested credentials
            const mpesaData = mpesaResponse.data.data || {};
            setSettings(prev => ({ 
                ...prev, 
                mpesa: mpesaData.credentials || mpesaData 
            }));
        } catch (e) {
            console.log('No M-Pesa credentials found');
        }
        
        try {
            const atResponse = await api.get('/tenants/credentials/africastalking');
            const atData = atResponse.data.data || {};
            setSettings(prev => ({ 
                ...prev, 
                africastalking: atData.credentials || atData 
            }));
        } catch (e) {
            console.log('No Africa\'s Talking credentials found');
        }
    } catch (error) {
        console.error('Error fetching settings:', error);
    } finally {
        setLoading(false);
    }
};

    const saveCompanySettings = async () => {
        setSaving(true);
        try {
            await api.put('/tenants/current', settings.company);
            alert('Company settings saved successfully');
        } catch (error) {
            console.error('Error saving company settings:', error);
            alert('Failed to save company settings');
        } finally {
            setSaving(false);
        }
    };

    const saveMpesaSettings = async () => {
    setSaving(true);
    try {
        // Send credentials in the correct format
        const payload = {
            credentials: {
                consumer_key: settings.mpesa.consumer_key,
                consumer_secret: settings.mpesa.consumer_secret,
                shortcode: settings.mpesa.shortcode,
                paybill_number: settings.mpesa.paybill_number,
                till_number: settings.mpesa.till_number,
                passkey: settings.mpesa.passkey,
                environment: settings.mpesa.environment || 'sandbox'
            }
        };
        
        console.log('Sending M-Pesa credentials:', {
            ...payload,
            credentials: { ...payload.credentials, consumer_secret: '***', passkey: '***' }
        });
        
        await api.post('/tenants/credentials/mpesa', payload);
        alert('M-Pesa credentials saved successfully');
    } catch (error) {
        console.error('Error saving M-Pesa settings:', error);
        alert(error.response?.data?.error || 'Failed to save M-Pesa credentials');
    } finally {
        setSaving(false);
    }
};

    const saveAfricaSTalkingSettings = async () => {
    setSaving(true);
    try {
        const payload = {
            credentials: {
                api_key: settings.africastalking.api_key,
                username: settings.africastalking.username,
                sender_id: settings.africastalking.sender_id
            }
        };
        
        await api.post('/tenants/credentials/africastalking', payload);
        alert('Africa\'s Talking credentials saved successfully');
    } catch (error) {
        console.error('Error saving Africa\'s Talking settings:', error);
        alert(error.response?.data?.error || 'Failed to save Africa\'s Talking credentials');
    } finally {
        setSaving(false);
    }
};

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                <p className="text-gray-600">Manage your account and integration settings</p>
            </div>
            
            <div className="space-y-6">
                {/* Company Settings */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                        <div className="flex items-center">
                            <Globe className="h-6 w-6 text-indigo-600 mr-2" />
                            <h2 className="text-xl font-semibold">Company Settings</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Name
                                </label>
                                <input
                                    type="text"
                                    value={settings.company.company_name || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        company: { ...settings.company, company_name: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Subdomain
                                </label>
                                <input
                                    type="text"
                                    value={settings.company.subdomain || ''}
                                    disabled
                                    className="w-full rounded-lg border border-gray-300 bg-gray-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Currency
                                </label>
                                <select
                                    value={settings.company.settings?.currency || 'KES'}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        company: {
                                            ...settings.company,
                                            settings: { ...settings.company.settings, currency: e.target.value }
                                        }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="KES">Kenyan Shilling (KES)</option>
                                    <option value="USD">US Dollar (USD)</option>
                                    <option value="UGX">Ugandan Shilling (UGX)</option>
                                    <option value="TZS">Tanzanian Shilling (TZS)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Timezone
                                </label>
                                <select
                                    value={settings.company.settings?.timezone || 'Africa/Nairobi'}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        company: {
                                            ...settings.company,
                                            settings: { ...settings.company.settings, timezone: e.target.value }
                                        }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                                    <option value="Africa/Johannesburg">South Africa Standard Time</option>
                                    <option value="Africa/Cairo">Egypt Standard Time</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={saveCompanySettings}
                                disabled={saving}
                                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                                <Save className="h-5 w-5 mr-2" />
                                Save Company Settings
                            </button>
                        </div>
                    </div>
                </div>

                {/* M-Pesa Integration */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                        <div className="flex items-center">
                            <Smartphone className="h-6 w-6 text-green-600 mr-2" />
                            <h2 className="text-xl font-semibold">M-Pesa Integration</h2>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Configure your M-Pesa API credentials for automated payments</p>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Consumer Key
                                </label>
                                <input
                                    type="text"
                                    value={settings.mpesa.consumer_key || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, consumer_key: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter M-Pesa Consumer Key"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Consumer Secret
                                </label>
                                <input
                                    type="password"
                                    value={settings.mpesa.consumer_secret || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, consumer_secret: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter M-Pesa Consumer Secret"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Shortcode (Paybill)
                                </label>
                                <input
                                    type="text"
                                    value={settings.mpesa.shortcode || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, shortcode: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., 174379"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Paybill Number (for tenants)
                                </label>
                                <input
                                    type="text"
                                    value={settings.mpesa.paybill_number || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, paybill_number: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., 247247"
                                />
                                <p className="text-xs text-gray-400 mt-1">Shown to tenants for manual M-Pesa payments</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Till Number
                                </label>
                                <input
                                    type="text"
                                    value={settings.mpesa.till_number || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, till_number: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., 123456"
                                />
                                <p className="text-xs text-gray-400 mt-1">Alternative to Paybill for Buy Goods Till</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Passkey
                                </label>
                                <input
                                    type="password"
                                    value={settings.mpesa.passkey || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, passkey: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter M-Pesa Passkey"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Environment
                                </label>
                                <select
                                    value={settings.mpesa.environment || 'sandbox'}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        mpesa: { ...settings.mpesa, environment: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="sandbox">Sandbox (Testing)</option>
                                    <option value="production">Production (Live)</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={saveMpesaSettings}
                                disabled={saving}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                <Key className="h-5 w-5 mr-2" />
                                Save M-Pesa Credentials
                            </button>
                        </div>
                    </div>
                </div>

                {/* Africa's Talking Integration */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                        <div className="flex items-center">
                            <Mail className="h-6 w-6 text-orange-600 mr-2" />
                            <h2 className="text-xl font-semibold">Africa's Talking Integration</h2>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Configure SMS and Voice notification settings</p>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    API Key
                                </label>
                                <input
                                    type="password"
                                    value={settings.africastalking.api_key || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        africastalking: { ...settings.africastalking, api_key: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter API Key"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Username
                                </label>
                                <input
                                    type="text"
                                    value={settings.africastalking.username || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        africastalking: { ...settings.africastalking, username: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter Username"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Sender ID (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={settings.africastalking.sender_id || ''}
                                    onChange={(e) => setSettings({
                                        ...settings,
                                        africastalking: { ...settings.africastalking, sender_id: e.target.value }
                                    })}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Your Brand Name"
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={saveAfricaSTalkingSettings}
                                disabled={saving}
                                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                <Key className="h-5 w-5 mr-2" />
                                Save Africa's Talking Credentials
                            </button>
                        </div>
                    </div>
                </div>

                {/* Notification Settings */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                        <div className="flex items-center">
                            <Bell className="h-6 w-6 text-yellow-600 mr-2" />
                            <h2 className="text-xl font-semibold">Notification Settings</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">Email Notifications</p>
                                    <p className="text-sm text-gray-500">Receive payment receipts and reports via email</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.notifications.email_enabled || true}
                                        onChange={(e) => setSettings({
                                            ...settings,
                                            notifications: { ...settings.notifications, email_enabled: e.target.checked }
                                        })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">SMS Notifications</p>
                                    <p className="text-sm text-gray-500">Send rent reminders via SMS</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.notifications.sms_enabled || true}
                                        onChange={(e) => setSettings({
                                            ...settings,
                                            notifications: { ...settings.notifications, sms_enabled: e.target.checked }
                                        })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">Rent Reminders</p>
                                    <p className="text-sm text-gray-500">Send automatic reminders 7, 3, and 1 day before due date</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.notifications.rent_reminders || true}
                                        onChange={(e) => setSettings({
                                            ...settings,
                                            notifications: { ...settings.notifications, rent_reminders: e.target.checked }
                                        })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">Lease Expiry Alerts</p>
                                    <p className="text-sm text-gray-500">Get notified when leases are about to expire</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.notifications.lease_expiry_alerts || true}
                                        onChange={(e) => setSettings({
                                            ...settings,
                                            notifications: { ...settings.notifications, lease_expiry_alerts: e.target.checked }
                                        })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        await api.put('/tenants/current', {
                                            settings: { notifications: settings.notifications }
                                        });
                                        alert('Notification settings saved successfully');
                                    } catch (error) {
                                        console.error('Error saving notification settings:', error);
                                        alert('Failed to save notification settings');
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                disabled={saving}
                                className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                            >
                                <Bell className="h-5 w-5 mr-2" />
                                Save Notification Settings
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;